import React, { useState } from 'react';
import { MapPin, Crosshair } from '@phosphor-icons/react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';

/**
 * Reusable city / current-location picker.
 *
 * Behaviour:
 *  • Renders a labelled text input with a "Use my location" button.
 *  • The button uses the browser Geolocation API → reverse-geocodes via
 *    OpenStreetMap Nominatim (no API key required) → fills the input with
 *    the detected city name.
 *  • Falls back gracefully when permission is denied or the API fails — the
 *    user can always type the city by hand.
 *
 * Props:
 *  - value, onChange — controlled value (string)
 *  - label          — optional override (default "City / Current Location")
 *  - required       — show red asterisk
 *  - testId         — base data-testid for the input/button pair
 *  - placeholder    — optional input placeholder
 */
const CityLocationInput = ({
  value,
  onChange,
  onGeoChange,           // optional ({ lat, lng, area, full_address }) — fired on geolocation detect
  label = 'City / Current Location',
  required = false,
  testId = 'city-location',
  placeholder = 'e.g. Chennai',
  cities = null,         // when provided, render a dropdown restricted to this list
}) => {
  const [detecting, setDetecting] = useState(false);

  const tryMatchCity = (detected) => {
    if (!cities) return detected;
    const lc = (detected || '').toLowerCase();
    return cities.find((c) => c.toLowerCase() === lc) || '';
  };

  const detectLocation = () => {
    if (!('geolocation' in navigator)) {
      toast.error('Geolocation is not supported on this device');
      return;
    }
    setDetecting(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        try {
          const r = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}&zoom=10&accept-language=en`,
            { headers: { 'Accept': 'application/json' } },
          );
          const data = await r.json();
          const a = data?.address || {};
          const city = a.city || a.town || a.village || a.suburb || a.county || a.state_district || a.state;
          // Area / locality — most specific human-readable area name.
          const area = a.suburb || a.neighbourhood || a.village || a.town || a.city_district || '';
          const full_address = data?.display_name || '';
          if (city) {
            const matched = tryMatchCity(city);
            if (matched) {
              onChange(matched);
              if (onGeoChange) onGeoChange({ lat: latitude, lng: longitude, area, full_address, detected_city: city });
              toast.success(`Location set to ${matched}${area ? ` · ${area}` : ''}`);
            } else if (cities) {
              toast.error(`We don't serve ${city} yet — please pick a city we cover`);
            } else {
              onChange(city);
              if (onGeoChange) onGeoChange({ lat: latitude, lng: longitude, area, full_address, detected_city: city });
              toast.success(`Location set to ${city}${area ? ` · ${area}` : ''}`);
            }
          } else {
            toast.error("Couldn't detect your city — please pick one");
          }
        } catch {
          toast.error('Reverse-geocoding failed — please pick your city');
        }
        setDetecting(false);
      },
      (err) => {
        const msg = err.code === err.PERMISSION_DENIED
          ? 'Permission denied — please pick your city'
          : 'Could not detect location';
        toast.error(msg);
        setDetecting(false);
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 },
    );
  };

  return (
    <div className="space-y-2">
      <Label className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary flex items-center gap-1">
        <MapPin size={14} /> {label}
        {required && <span className="text-red-500">*</span>}
      </Label>
      <div className="flex gap-2">
        {Array.isArray(cities) && cities.length > 0 ? (
          <select
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            className="rounded-none border border-[#E8E1D5] h-12 text-lg flex-1 px-2 bg-white"
            data-testid={`${testId}-select`}
          >
            <option value="">— Pick your city —</option>
            {cities.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        ) : (
          <Input
            type="text"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="rounded-none border-[#E8E1D5] h-12 text-lg flex-1"
            data-testid={`${testId}-input`}
          />
        )}
        <button
          type="button"
          onClick={detectLocation}
          disabled={detecting}
          className="px-3 h-12 border border-[#E8E1D5] bg-white hover:bg-brand-accent/10 transition-colors flex items-center gap-1.5 text-xs uppercase font-bold tracking-wider text-brand-primary disabled:opacity-60"
          data-testid={`${testId}-detect-btn`}
        >
          <Crosshair size={14} weight={detecting ? 'fill' : 'regular'} />
          {detecting ? 'Detecting...' : 'Use my location'}
        </button>
      </div>
      {Array.isArray(cities) && cities.length > 0 && !value && (
        <p className="text-[10px] text-text-secondary italic">
          Only cities BeeWeddy serves are shown. Don't see your city? Ask admin to add it.
        </p>
      )}
    </div>
  );
};

export default CityLocationInput;
